export type DiffLineType = 'unchanged' | 'added' | 'removed';

export interface DiffLine {
  type: DiffLineType;
  text: string;
  oldLineNumber: number | null;
  newLineNumber: number | null;
}

export interface DiffHunk {
  id: string;
  lines: DiffLine[];
  oldStart: number;
  newStart: number;
  hasChanges: boolean;
}

function lcsLengths(a: string[], b: string[]): number[][] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  return dp;
}

function backtrack(dp: number[][], a: string[], b: string[]): DiffLine[] {
  const result: DiffLine[] = [];
  let i = a.length;
  let j = b.length;
  let oldLine = a.length;
  let newLine = b.length;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      result.unshift({
        type: 'unchanged',
        text: a[i - 1],
        oldLineNumber: oldLine,
        newLineNumber: newLine,
      });
      i--;
      j--;
      oldLine--;
      newLine--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({
        type: 'added',
        text: b[j - 1],
        oldLineNumber: null,
        newLineNumber: newLine,
      });
      j--;
      newLine--;
    } else if (i > 0) {
      result.unshift({
        type: 'removed',
        text: a[i - 1],
        oldLineNumber: oldLine,
        newLineNumber: null,
      });
      i--;
      oldLine--;
    }
  }

  return result;
}

export function computeDiff(originalText: string, rewrittenText: string): DiffLine[] {
  const originalLines = originalText.split('\n');
  const rewrittenLines = rewrittenText.split('\n');
  const dp = lcsLengths(originalLines, rewrittenLines);
  return backtrack(dp, originalLines, rewrittenLines);
}

export function groupIntoHunks(diffLines: DiffLine[], contextLines: number = 3): DiffHunk[] {
  const hunks: DiffHunk[] = [];
  const changeIndices: number[] = [];

  diffLines.forEach((line, i) => {
    if (line.type !== 'unchanged') {
      changeIndices.push(i);
    }
  });

  if (changeIndices.length === 0) {
    return [{
      id: 'hunk_0',
      lines: diffLines.slice(0, Math.min(6, diffLines.length)),
      oldStart: 1,
      newStart: 1,
      hasChanges: false,
    }];
  }

  const ranges: [number, number][] = [];
  let start = Math.max(0, changeIndices[0] - contextLines);
  let end = Math.min(diffLines.length - 1, changeIndices[0] + contextLines);

  for (let k = 1; k < changeIndices.length; k++) {
    const nextStart = Math.max(0, changeIndices[k] - contextLines);
    const nextEnd = Math.min(diffLines.length - 1, changeIndices[k] + contextLines);

    if (nextStart <= end + 1) {
      end = nextEnd;
    } else {
      ranges.push([start, end]);
      start = nextStart;
      end = nextEnd;
    }
  }
  ranges.push([start, end]);

  ranges.forEach(([s, e], idx) => {
    const lines = diffLines.slice(s, e + 1);
    const firstOld = lines.find(l => l.oldLineNumber !== null);
    const firstNew = lines.find(l => l.newLineNumber !== null);

    hunks.push({
      id: `hunk_${idx}`,
      lines,
      oldStart: firstOld?.oldLineNumber ?? 1,
      newStart: firstNew?.newLineNumber ?? 1,
      hasChanges: lines.some(l => l.type !== 'unchanged'),
    });
  });

  return hunks;
}
